import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Heading, Text } from '@chakra-ui/react';
import { describeError, reportError } from '../lib/errors';

// The last line of defence (M67, D254): a render error anywhere in the app
// shows this card instead of a blank page, and is reported to Twinden's
// own server without anything identifying. English only: the i18n layer
// may be what broke.

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    const described = describeError(error);
    reportError({
      message: `Render error: ${described.message}`,
      stack: described.stack ?? info.componentStack ?? undefined,
    });
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Box
        minH="100vh"
        bg="canvas"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={4}
      >
        <Box
          bg="surface"
          borderWidth="1px"
          borderColor="line"
          borderRadius="card"
          p={6}
          maxW="420px"
          w="full"
          textAlign="center"
          role="alert"
        >
          <Heading size="md" mb={2}>
            Something went wrong
          </Heading>
          <Text color="muted" fontSize="sm" mb={4}>
            Twinden hit a problem it couldn&apos;t recover from. Your messages
            are safe. Reloading usually fixes it.
          </Text>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </Box>
      </Box>
    );
  }
}
